import { Comment, UpdateComment } from "@app/types";
import { Images } from "@assets/images";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import HeartIcon from "@app/icons/heart";
import { useState } from "react";
import ReplyComment from "../reply-comment";
import { Form, Formik } from "formik";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Button from "@app/components/button";
import CommentService from "@app/services/http/comment.service";
import { addToast } from "@app/components/toast/toast.service";
import { SystemMessage } from "@app/constants";

dayjs.extend(relativeTime);
dayjs.locale("vi");

const formatRelativeDate = (timestamp: number) => {
  const date = dayjs(timestamp * 1000);

  return date.fromNow();
};

export interface CommentProps {
  comment: Comment[];
  onLikeComment: (commentId: number) => void;
  onDeleteComment: (commentId: number) => void;
  onReplyComment: (onReply: boolean) => void;
  onEditComment: (content: string) => void;
}

const CommentItem = ({
  comment,
  onLikeComment,
  onReplyComment,
  onEditComment,
  onDeleteComment,
}: CommentProps) => {
  const [activeReplies, setActiveReplies] = useState<{
    [key: number]: number | null;
  }>({});
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editingChildComment, setEditingChildComment] = useState<number | null>(
    null
  );

  const handleReplyComment = (status: boolean) => {
    onReplyComment(status);
  };

  const toggleReply = (commentId: number, childId?: number) => {
    setActiveReplies((prev) => ({
      ...prev,
      [childId ?? commentId]:
        prev[childId ?? commentId] === null ||
        prev[childId ?? commentId] === undefined
          ? childId ?? commentId
          : null,
    }));
  };

  const handleEditClick = (commentId: number, isChild = false) => {
    if (isChild) {
      setEditingChildComment(commentId);
    } else {
      setEditingComment(commentId);
    }
  };

  const handleEditCancel = (isChild = false) => {
    if (isChild) {
      setEditingChildComment(null);
    } else {
      setEditingComment(null);
    }
  };

  const onSubmit = (values: UpdateComment, isChild = false) => {
    if (!values.content) return;

    CommentService.editComment({
      commentId: values.commentId,
      content: values.content,
    }).then(() => {
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      onEditComment(values.content);

      if (isChild) {
        setEditingChildComment(null);
      } else {
        setEditingComment(null);
      }
    });
  };

  return (
    <div className="pt-4 space-y-6">
      {!!comment.length &&
        comment.map((item) => (
          <div key={item.id} className="text-gray-300 py-2 px-2">
            <div className="flex">
              <img
                src={item.myThumbnail}
                alt="Avatar"
                className="rounded-full w-10 h-10 mr-3"
              />

              <div className="w-full">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-base text-gray-200">
                      {item.myFullName}
                    </div>
                    {item.isPin && (
                      <img
                        src={Images.Pinned.default}
                        alt="Pinned"
                        className="mt-1 w-5 h-5"
                      />
                    )}
                    <div className="mt-2 text-sm text-gray-400 leading-relaxed">
                      {editingComment === item.id ? (
                        <Formik
                          initialValues={{
                            commentId: item.id,
                            content: item.content,
                          }}
                          onSubmit={(values) => onSubmit(values)}
                          enableReinitialize={false}
                        >
                          <Form>
                            <hr className="absolute w-[380px] left-[18px] top-0 border-t-[#4E586C]" />
                            <div className="flex items-center justify-evenly">
                              <FormControl name="content">
                                <Input
                                  className="!w-[220px]"
                                  inputClassName="!bg-[#506079] !h-[40px] !text-white font-[SVN-Gilroy]"
                                  placeholder="Bình luận"
                                />
                              </FormControl>
                              <Button
                                width="auto"
                                type="submit"
                                className="!w-[60px] !h-9 font-bold !text-[#6f98f0] !shadow-none !bg-transparent"
                                label="Lưu"
                                labelClassName="text-sm"
                              />
                            </div>
                          </Form>
                        </Formik>
                      ) : (
                        item.content
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => onLikeComment(item.id)}
                    className="hover:text-red-400 flex items-center space-x-1"
                  >
                    <HeartIcon isLiked={item.isLiked} />
                    <span>{item.likeCount}</span>
                  </button>
                </div>

                <div className="mt-3 flex items-center text-sm text-gray-400">
                  <div>{formatRelativeDate(item.createdDate)}</div>

                  <button
                    onClick={() => toggleReply(item.id)}
                    className="ml-6 text-blue-400 hover:underline"
                  >
                    Trả lời
                  </button>

                  <button
                    onClick={() => onDeleteComment(item.id)}
                    className="ml-6 text-red-400 hover:underline"
                  >
                    Xóa
                  </button>
                  {editingComment === item.id ? (
                    <>
                      <button
                        onClick={() => handleEditCancel()}
                        className="ml-6 text-gray-400 hover:underline"
                      >
                        Hủy
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditClick(item.id)}
                      className="ml-6 text-yellow-400 hover:underline"
                    >
                      Sửa
                    </button>
                  )}
                </div>
                {activeReplies[item.id] && (
                  <div className="mt-4">
                    <ReplyComment
                      commentId={item.id}
                      onReplyComment={handleReplyComment}
                      onCancel={() => toggleReply(item.id)}
                    />
                  </div>
                )}

                {item?.children && item?.children.length > 0 && (
                  <div className="mt-4">
                    {item.children.map((child) => (
                      <div key={child.id} className="text-gray-300 py-2 px-2">
                        <div className="flex">
                          <img
                            src={child.myThumbnail}
                            alt="Avatar"
                            className="rounded-full w-10 h-10 mr-3"
                          />

                          <div className="w-full">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-semibold text-base text-gray-200 flex items-center">
                                  <div>{child.myFullName}</div>
                                  {child.level === 3 && (
                                    <>
                                      <div>
                                        <img
                                          src={Images.Reply.default}
                                          alt="Reply"
                                          className="mt-1 w-3 h-3 ml-2"
                                        />
                                      </div>
                                      <div className="font-semibold text-base text-gray-200 ml-2">
                                        {child.ownerFullName}
                                      </div>
                                    </>
                                  )}
                                </div>

                                <div className="mt-2 text-sm text-gray-400 leading-relaxed">
                                  {editingChildComment === child.id ? (
                                    <Formik
                                      initialValues={{
                                        commentId: child.id,
                                        content: child.content,
                                      }}
                                      onSubmit={(values) =>
                                        onSubmit(values, true)
                                      }
                                      enableReinitialize={false}
                                    >
                                      <Form>
                                        <div className="flex items-center justify-evenly">
                                          <FormControl name="content">
                                            <Input
                                              className="!w-[180px]"
                                              inputClassName="!bg-[#506079] !h-[40px] !text-white font-[SVN-Gilroy]"
                                              placeholder="Bình luận"
                                            />
                                          </FormControl>
                                          <Button
                                            width="auto"
                                            type="submit"
                                            className="!w-[50px] !h-9 font-bold !text-[#6f98f0] !shadow-none !bg-transparent"
                                            label="Lưu"
                                            labelClassName="text-sm"
                                          />
                                        </div>
                                      </Form>
                                    </Formik>
                                  ) : (
                                    child.content
                                  )}
                                </div>
                              </div>

                              <button
                                onClick={() => onLikeComment(child.id)}
                                className="hover:text-red-400 flex items-center space-x-1"
                              >
                                <HeartIcon isLiked={child.isLiked} />
                                <span>{child.likeCount}</span>
                              </button>
                            </div>

                            <div className="mt-3 flex items-center text-sm text-gray-400">
                              <div>{formatRelativeDate(child.createdDate)}</div>

                              <button
                                onClick={() => toggleReply(item.id, child.id)}
                                className="ml-4 text-blue-400 hover:underline"
                              >
                                Trả lời
                              </button>

                              <button
                                onClick={() => onDeleteComment(child.id)}
                                className="ml-4 text-red-400 hover:underline"
                              >
                                Xóa
                              </button>
                              {editingChildComment === child.id ? (
                                <button
                                  onClick={() => handleEditCancel(true)}
                                  className="ml-4 text-gray-400 hover:underline"
                                >
                                  Hủy
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleEditClick(child.id, true)
                                  }
                                  className="ml-4 text-yellow-400 hover:underline"
                                >
                                  Sửa
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {activeReplies[child.id] && (
                          <div className="mt-4">
                            <ReplyComment
                              commentId={child.id}
                              onReplyComment={handleReplyComment}
                              onCancel={() => toggleReply(item.id, child.id)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

export default CommentItem;
